import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { sitesApi } from "../api/sites"

const initialState = {
  sites: [],
  currentSite: null,
  isLoading: false,
  error: null,
}

export const fetchSites = createAsyncThunk("sites/fetchSites", async (_, { rejectWithValue }) => {
  try {
    const response = await sitesApi.getAll()
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Ошибка загрузки сайтов")
  }
})

export const fetchSiteById = createAsyncThunk("sites/fetchSiteById", async (siteId, { rejectWithValue }) => {
  try {
    const response = await sitesApi.getById(siteId)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Ошибка загрузки сайта")
  }
})

export const createSite = createAsyncThunk("sites/createSite", async (siteData, { rejectWithValue }) => {
  try {
    const response = await sitesApi.create(siteData)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Ошибка создания сайта")
  }
})

export const updateSite = createAsyncThunk("sites/updateSite", async ({ siteId, data }, { rejectWithValue }) => {
  try {
    const response = await sitesApi.update(siteId, data)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Ошибка обновления сайта")
  }
})

export const deleteSite = createAsyncThunk("sites/deleteSite", async (siteId, { rejectWithValue }) => {
  try {
    await sitesApi.delete(siteId)
    return siteId
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Ошибка удаления сайта")
  }
})

const sitesSlice = createSlice({
  name: "sites",
  initialState,
  reducers: {
    setCurrentSite: (state, action) => {
      state.currentSite = action.payload
    },
    updateSiteInList: (state, action) => {
      const updatedSite = action.payload
      const siteIndex = state.sites.findIndex((s) => s.id === updatedSite.id)
      if (siteIndex !== -1) {
        // Создаем новый объект для правильного обновления в Immer
        state.sites[siteIndex] = { ...state.sites[siteIndex], ...updatedSite }
      }
      // Также обновить currentSite, если это тот же сайт
      if (state.currentSite?.id === updatedSite.id) {
        state.currentSite = { ...state.currentSite, ...updatedSite }
      }
    },
    clearSitesError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch sites
      .addCase(fetchSites.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchSites.fulfilled, (state, action) => {
        state.isLoading = false
        state.sites = action.payload.sites
      })
      .addCase(fetchSites.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
      // Fetch site by ID
      .addCase(fetchSiteById.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchSiteById.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentSite = action.payload.site
      })
      .addCase(fetchSiteById.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
      // Create site
      .addCase(createSite.fulfilled, (state, action) => {
        state.sites.push(action.payload.site)
      })
      // Update site
      .addCase(updateSite.fulfilled, (state, action) => {
        const updatedSite = action.payload.site
        const siteIndex = state.sites.findIndex((s) => s.id === updatedSite.id)
        if (siteIndex !== -1) {
          state.sites[siteIndex] = { ...state.sites[siteIndex], ...updatedSite }
        }
        if (state.currentSite?.id === updatedSite.id) {
          state.currentSite = { ...state.currentSite, ...updatedSite }
        }
      })
      // Delete site
      .addCase(deleteSite.fulfilled, (state, action) => {
        state.sites = state.sites.filter((site) => site.id !== action.payload)
        if (state.currentSite?.id === action.payload) {
          state.currentSite = null
        }
      })
  },
})

export const { setCurrentSite, updateSiteInList, clearSitesError } = sitesSlice.actions

export const selectSites = (state) => state.sites.sites
export const selectCurrentSite = (state) => state.sites.currentSite
export const selectSitesLoading = (state) => state.sites.isLoading
export const selectSitesError = (state) => state.sites.error

export default sitesSlice.reducer
